import { Routes, Route, Navigate } from "react-router-dom";
import { ConfigProvider, App as AntApp } from "antd";
import { Provider } from "react-redux";
import { store } from "./store/store";
import { useAuth } from "./hooks/useAuth";
import { useTheme } from "./contexts/ThemeContext";
import AppLayoutWrapper from "./components/AppLayoutWrapper";
import AppInitializer from "./components/AppInitializer";
import LoadingSpinner from "./components/LoadingSpinner";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ProjectsPage from "./pages/ProjectsPage";
import ChatPage from "./pages/ChatPage";
import SettingsPage from "./pages/SettingsPage";
import StateGraphPage from "./pages/StateGraphPage";
import QueryGeneratorPage from "./pages/QueryGeneratorPage";
import QueryLogsPage from "./pages/QueryLogsPage";
import FileManagementPage from "./pages/FileManagementPage";
import DemoEnrollmentForm from "./pages/DemoEnrollmentForm";
import FeedbackKioskPage from "./pages/Demo/FeedbackKioskPage";
import ComprehensiveFeedbackPage from "./pages/Demo/ComprehensiveFeedbackPage";
import DemoFeedbackPage from "./pages/DemoFeedbackPage";
import MaximoPage from "./pages/MaximoPage";
import AppConfigurationPage from "./pages/AppConfigurationPage";
import TrainingDashboard from "./pages/TrainingDashboard";
import TrainingStudentsPage from "./pages/TrainingStudentsPage";
import TrainingSettingsPage from "./pages/TrainingSettingsPageNew";
import TrainingAppConfigurationPage from "./pages/TrainingAppConfigurationPage";
import DatasetListPage from "./pages/Training/DatasetListPage";
import SchemaGeneratorPage from "./pages/Training/SchemaGeneratorPage";
import SchemaUploadPage from "./pages/Training/SchemaUploadPage";
import DatasetSearchPage from "./pages/Training/DatasetSearchPage";
import TrainingFileStoragePage from "./pages/Training/TrainingFileStoragePage";
import GpuInstancesPage from "./pages/Training/GpuInstancesPage";
import ModelsPage from "./pages/Training/ModelsPage";
import InstanceConnectPage from "./pages/Training/InstanceConnectPage";
import DemoMaximoWorkOrders from "./pages/Demo/DemoMaximoWorkOrders";
import DemoMaximoCalendar from "./pages/Demo/DemoMaximoCalendar";
import DemoAppConfigurationPage from "./pages/Demo/DemoAppConfigurationPage";
import DemoSettingsPage from "./pages/Demo/DemoSettingsPage";
import StateGraphRedirect from "./components/StateGraphRedirect";
import FallbackAppDashboard from "./pages/FallbackAppDashboard";
import FallbackAppSettings from "./pages/FallbackAppSettings";
import FallbackAppApiPlayground from "./pages/FallbackAppApiPlayground";
import SystemWorkflowsPage from "./pages/SystemWorkflowsPage";
import WidgetsPage from "./pages/WidgetsPage";
import IntegrationsPage from "./pages/IntegrationsPage";
import AppStorePage from "./pages/AppStorePage";
import IAMPage from "./pages/IAM";
import ProfilePage from "./pages/ProfilePage";
import AppStoreSettingsPage from "./pages/AppStoreSettingsPage";
import ModuleBuilderPage from "./pages/Demo/ModuleBuilderPage";
import FieldBuilderPage from "./pages/Demo/FieldBuilderPage";
import DynamicRecordsPage from "./pages/Demo/DynamicRecordsPage";
import McpServerPage from "./pages/McpServer/McpServerPage";
import FeedbackDashboard from "./pages/Feedback/FeedbackDashboard";
import EventsList from "./pages/Feedback/EventsList";
import EventDetails from "./pages/Feedback/EventDetails";
import EventAnalytics from "./pages/Feedback/EventAnalytics";
import EmailTemplatesList from "./pages/Feedback/EmailTemplatesList";
import FeedbackAppConfiguration from "./pages/Feedback/FeedbackAppConfiguration";
import FeedbackSettings from "./pages/Feedback/FeedbackSettings";
import FormsList from "./pages/Feedback/FormsList";
import FormTemplatesList from "./pages/Feedback/FormTemplatesList";
import FormBuilderPage from "./pages/Feedback/FormBuilderPage";
import PublicFeedbackForm from "./pages/Feedback/PublicFeedbackForm";

const AppContent = () => {
  const { user, isLoading } = useAuth();
  const { getAntdTheme } = useTheme();

  if (isLoading) {
    return (
      <ConfigProvider theme={getAntdTheme()}>
        <div className="min-h-screen flex items-center justify-center">
          <LoadingSpinner size="large" />
        </div>
      </ConfigProvider>
    );
  }

  if (!user) {
    return (
      <ConfigProvider theme={getAntdTheme()}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/demo/enroll" element={<DemoEnrollmentForm />} />
          <Route path="/demo/feedback" element={<ComprehensiveFeedbackPage />} />
          <Route path="/demo/feedback/kiosk" element={<FeedbackKioskPage />} />
          <Route path="/feedback-form/:token" element={<PublicFeedbackForm />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </ConfigProvider>
    );
  }

  return (
    <Provider store={store}>
      <ConfigProvider theme={getAntdTheme()}>
        <AntApp>
          <AppInitializer>
            <Routes>
              {/* Standalone pages without app layout */}
              <Route path="/training/gpu-instances/:instanceId/connect" element={<InstanceConnectPage />} />
              
              {/* All other pages with app layout */}
              <Route path="*" element={
                <AppLayoutWrapper>
                  <Routes>
              <Route path="/" element={<Navigate to="/app" replace />} />
              <Route path="/login" element={<Navigate to="/app" replace />} />
              <Route path="/register" element={<Navigate to="/app" replace />} />
              <Route path="/admin" element={<Navigate to="/admin/projects" replace />} />
              <Route path="/admin/projects" element={<ProjectsPage />} />
              <Route path="/admin/query-generator" element={<QueryGeneratorPage />} />
              <Route path="/admin/chat" element={<ChatPage />} />
              <Route path="/admin/widgets" element={<WidgetsPage />} />
              <Route path="/admin/integrations" element={<IntegrationsPage />} />
              <Route path="/admin/files" element={<FileManagementPage />} />
              <Route path="/admin/query-logs" element={<QueryLogsPage />} />
              <Route path="/admin/demo-feedback" element={<DemoFeedbackPage />} />
              <Route path="/admin/maximo" element={<MaximoPage />} />
              <Route path="/admin/app-configuration" element={<AppConfigurationPage />} />
              <Route path="/admin/mcp-server" element={<McpServerPage />} />
              <Route path="/admin/system-workflows" element={<SystemWorkflowsPage />} />
              <Route path="/admin/settings" element={<SettingsPage />} />
              <Route
                path="/admin/projects/:projectId/state-graph"
                element={<StateGraphPage />}
              />
              {/* Training Application Routes */}
              <Route path="/training" element={<Navigate to="/training/dashboard" replace />} />
              <Route path="/training/dashboard" element={<TrainingDashboard />} />
              <Route path="/training/datasets" element={<Navigate to="/training/datasets/list" replace />} />
              <Route path="/training/datasets/list" element={<DatasetListPage />} />
              <Route path="/training/datasets/generate" element={<SchemaGeneratorPage />} />
              <Route path="/training/datasets/upload" element={<SchemaUploadPage />} />
              <Route path="/training/datasets/:id/search" element={<DatasetSearchPage />} />
              <Route path="/training/fine-tuning" element={<TrainingStudentsPage />} />
              <Route path="/training/jobs" element={<TrainingSettingsPage />} />
              <Route path="/training/files" element={<TrainingFileStoragePage />} />
              <Route path="/training/gpu-instances" element={<GpuInstancesPage />} />
              <Route path="/training/models" element={<ModelsPage />} />
              <Route path="/training/app-configuration" element={<TrainingAppConfigurationPage />} />
              <Route path="/training/settings" element={<TrainingSettingsPage />} />
              {/* Feedback Application Routes */}
              <Route path="/feedback" element={<Navigate to="/feedback/dashboard" replace />} />
              <Route path="/feedback/dashboard" element={<FeedbackDashboard />} />
              <Route path="/feedback/events" element={<EventsList />} />
              <Route path="/feedback/events/:eventId" element={<EventDetails />} />
              <Route path="/feedback/events/:eventId/analytics" element={<EventAnalytics />} />
              <Route path="/feedback/email-templates" element={<EmailTemplatesList />} />
              <Route path="/feedback/forms" element={<FormsList />} />
              <Route path="/feedback/forms/new" element={<FormBuilderPage />} />
              <Route path="/feedback/forms/:formId/edit" element={<FormBuilderPage />} />
              <Route path="/feedback/form-templates" element={<FormTemplatesList />} />
              <Route path="/feedback/form-templates/:formId/edit" element={<FormBuilderPage />} />
              <Route path="/feedback/app-configuration" element={<FeedbackAppConfiguration />} />
              <Route path="/feedback/settings" element={<FeedbackSettings />} />
              {/* Demo Application Routes */}
              <Route path="/demo" element={<Navigate to="/demo/maximo/workorders" replace />} />
              <Route path="/demo/maximo/workorders" element={<DemoMaximoWorkOrders />} />
              <Route path="/demo/maximo/calendar" element={<DemoMaximoCalendar />} />
              <Route path="/demo/app-configuration" element={<DemoAppConfigurationPage />} />
              <Route path="/demo/settings" element={<DemoSettingsPage />} />
              {/* Low-Code Module Builder Routes */}
              <Route path="/demo/module-builder" element={<ModuleBuilderPage />} />
              <Route path="/demo/module-builder/:moduleId/fields" element={<FieldBuilderPage />} />
              <Route path="/demo/:moduleKey/records" element={<DynamicRecordsPage />} />
              {/* Fallback Application Routes */}
              <Route path="/fallbackapp" element={<Navigate to="/fallbackapp/dashboard" replace />} />
              <Route path="/fallbackapp/dashboard" element={<FallbackAppDashboard />} />
              <Route path="/fallbackapp/settings" element={<FallbackAppSettings />} />
              <Route path="/fallbackapp/api-playground" element={<FallbackAppApiPlayground />} />
              <Route path="/fallbackapp/system-workflows" element={<SystemWorkflowsPage />} />
              {/* AppStore Routes */}
              <Route path="/app" element={<AppStorePage />} />
              <Route path="/app/profile" element={<ProfilePage />} />
              <Route path="/app/settings" element={<AppStoreSettingsPage />} />
              {/* IAM Application Routes */}
              <Route path="/iam" element={<IAMPage />} />
              <Route path="/iam/*" element={<IAMPage />} />
              {/* Legacy routes - redirect to admin routes */}
              <Route path="/projects" element={<Navigate to="/admin/projects" replace />} />
              <Route path="/query-generator" element={<Navigate to="/admin/query-generator" replace />} />
              <Route path="/chat" element={<Navigate to="/admin/chat" replace />} />
              <Route path="/files" element={<Navigate to="/admin/files" replace />} />
              <Route path="/query-logs" element={<Navigate to="/admin/query-logs" replace />} />
              <Route path="/demo-feedback" element={<Navigate to="/admin/demo-feedback" replace />} />
              <Route path="/maximo" element={<Navigate to="/admin/maximo" replace />} />
              <Route path="/app-configuration" element={<Navigate to="/admin/app-configuration" replace />} />
              <Route path="/settings" element={<Navigate to="/admin/settings" replace />} />
              <Route path="/projects/:projectId/state-graph" element={<StateGraphRedirect />} />
              <Route path="*" element={<Navigate to="/admin/projects" replace />} />
              </Routes>
                </AppLayoutWrapper>
              } />
            </Routes>
          </AppInitializer>
        </AntApp>
      </ConfigProvider>
    </Provider>
  );
};

function App() {
  return <AppContent />;
}

export default App;
